import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import productAPI from "../../api/productApi";
import NotFound from "../errors/NotFound";
import { InlineLoader } from "../../context/PageLoader";
import Breadcrump from "../../components/Breadcrump";
import SEO from "../../components/SEO";
import ProductCard from "./Card";
import { Heart, ShoppingBag, Play, Share2, Link2 } from "lucide-react";
import Auth from "../../utils/AuthUtils";
import wishlistAPI from "../../api/wishlistApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import DynamicField from "../cmsPage/DynamicField";
import {
  extractProductVideo,
  isYouTubeUrl,
  getYouTubeId,
} from "../../utils/videoHelper";


/******************* VIDEO HELPERS *******************/
const isYouTube = isYouTubeUrl;
const getYouTubeID = getYouTubeId;

const isVideoUrl = (url = "") =>
  isYouTubeUrl(url) || /\.(mp4|webm|mov)(\?|$)/i.test(url);

function DetailPage() {
  const { slug } = useParams();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [detail, setDetail] = useState(null);
  const [variants, setVariants] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [displayedImages, setDisplayedImages] = useState([]);

  const [mainImage, setMainImage] = useState(null); // image ya video, dono ka src
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const [title, setTitle] = useState();
  const [brand, setBrand] = useState({});
  const [skuCode, setSkuCode] = useState("");
  const [category, setCategory] = useState({});
  const [description, setDescription] = useState("");

  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (product?.minimumOrderQuantity) {
      setQuantity(product.minimumOrderQuantity);
    }
  }, [product]);

  /******************* FETCH PRODUCT *******************/
  const fetchProductDetail = async () => {
    try {
      setNotFound(false);
      const res = await productAPI.details(slug);

      if (res.code === 200) {
        const {
          product,
          detail,
          brand,
          category,
          variants,
          relatedProducts,
          isWishlisted,
        } = res.data;

        setProduct(product);
        setIsWishlisted(isWishlisted || false);
        setDetail(detail);
        setBrand(brand);
        setCategory(category);
        setVariants(variants || []);
        setRelatedProducts(relatedProducts || []);

        setTitle(product?.title);
        setSkuCode(product?.skuCode);
        setDescription(detail?.description);

        if (variants?.length > 0) {
          setSelectedVariant(variants[0]);
        }

        const images = [];
        if (product?.thumbnail) images.push(product.thumbnail);
        if (detail?.images?.length) images.push(...detail.images);
        if (variants?.length) {
          variants.forEach((v) => {
            if (v.images?.length) images.push(...v.images);
          });
        }

        if (images.length > 0) {
          setMainImage(images[0]);
          setDisplayedImages(images);
        }
      } else {
        setNotFound(true);
      }
    } catch (err) {
      console.error(err);
      setNotFound(true);
    }
  };

  useEffect(() => {
    fetchProductDetail();
  }, [slug]);

  /******************* VARIANT ATTRIBUTES *******************/
  const attributeMap = {};

  variants.forEach((variant) => {
    if (variant.attributes && typeof variant.attributes === "object") {
      Object.entries(variant.attributes).forEach(([key, value]) => {
        if (!attributeMap[key]) attributeMap[key] = new Set();
        attributeMap[key].add(value);
      });
    }
  });

  const handleVariantClick = (key, value) => {
    const updated = { ...selectedAttributes, [key]: value };
    setSelectedAttributes(updated);

    const matched = variants.find((v) =>
      Object.entries(updated).every(([k, val]) => v.attributes?.[k] === val),
    );

    if (matched) {
      setSelectedVariant(matched);

      if (matched.images?.length) {
        setDisplayedImages(matched.images);
        setMainImage(matched.images[0]);
      }
    }
  };

  // Default attributes (variants load hone ke baad)
  useEffect(() => {
    const defaultSelected = {};
    Object.entries(attributeMap).forEach(([key, valueSet]) => {
      const values = Array.from(valueSet);
      if (values.length > 0) defaultSelected[key] = values[0];
    });
    setSelectedAttributes(defaultSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variants]);

  // URL query params se pre-select (?Size=M&Color=Red)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const preSelectedAttributes = {};

    for (const [key, value] of params.entries()) {
      preSelectedAttributes[key] = value;
    }

    if (Object.keys(preSelectedAttributes).length > 0 && variants.length > 0) {
      setSelectedAttributes(preSelectedAttributes);

      const matched = variants.find((variant) =>
        Object.entries(preSelectedAttributes).every(
          ([key, val]) => variant.attributes?.[key] === val,
        ),
      );

      if (matched) {
        setSelectedVariant(matched);
        setTitle(`${product?.title} - ${matched.variant_name}`);
        setSkuCode(matched.sku || product?.skuCode);
        setDescription(matched.description || detail?.description);

        if (matched.images?.length) {
          setDisplayedImages(matched.images);
          setMainImage(matched.images[0]);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variants]);

  // Sticky bar / floating video toggle on scroll
  useEffect(() => {
    const handleScroll = () => {
      const quantityEl = document.querySelector(".action-btn-detail");
      const stickyEl = document.querySelector(".tf-sticky-btn-atc");
      const floatingVideo = document.querySelector(".floating-animate");

      if (!quantityEl || !stickyEl || !floatingVideo) return;

      const quantityBottom = quantityEl.getBoundingClientRect().bottom;

      if (quantityBottom <= window.innerHeight) {
        stickyEl.classList.remove("show");
        floatingVideo.classList.add("active");
      } else {
        stickyEl.classList.add("show");
        floatingVideo.classList.remove("active");
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /******************* CART *******************/
  const handleAddToCart = (e) => {
    e.preventDefault();

    const minQty = product?.minimumOrderQuantity || 1;

    const missingField = product?.customFields?.find(
      (field) =>
        field.required &&
        (!formData[field.key] || String(formData[field.key]).trim() === ""),
    );

    if (missingField) {
      showError(`${missingField.label} is required`);
      return;
    }

    if (quantity < minQty) {
      showError(`Minimum order quantity is ${minQty}`);
      return;
    }

    const action = e.currentTarget.dataset.action;

    try {
      addToCart(product._id, quantity, selectedVariant?._id, formData);

      if (action === "cart") {
        const offcanvasEl = document.getElementById("shoppingCartCanvas");

        if (offcanvasEl && window.bootstrap) {
          const offcanvas =
            window.bootstrap.Offcanvas.getOrCreateInstance(offcanvasEl);
          offcanvas.show();
        }
      }

      if (action === "buy-now") {
        navigate("/checkout");
      }
    } catch (err) {
      console.error(err);
    }
  };

  /******************* LOADING *******************/
  if (!product) return notFound ? <NotFound /> : <InlineLoader />;

  /******************* DERIVED *******************/
  const price = selectedVariant ? selectedVariant.price : detail?.pricing?.minPrice;

  // Video backend se (same helper jo pehle page mein use ho raha tha)
  const productVideo = extractProductVideo({ ...product, detail });

  // Images + video, dono thumbnails mein
  const mediaItems = [
    ...displayedImages.map((src) => ({ type: "image", src })),
    ...(productVideo ? [{ type: "video", src: productVideo }] : []),
  ];

  const isMainVideo =
    !!mainImage && (mainImage === productVideo || isVideoUrl(mainImage));

  /******************* WISHLIST *******************/
  const handleWishlistToggle = async (e) => {
    e.preventDefault();

    if (!Auth.getToken()) {
      showError("Please log in to manage your wishlist");
      return;
    }

    try {
      const res = await wishlistAPI.addProduct({ productId: product?._id });
      if (res.code === 200) {
        setIsWishlisted((prev) => !prev);
      }
    } catch (error) {
      showError("Failed to update wishlist");
    }
  };

  /******************* SHARE *******************/
  const shareUrl = window.location.href;
  const shareText = `Check out ${title} 👇`;

  const handleShare = async () => {
    // Mobile: native share sheet (WhatsApp, Instagram, etc.)
    if (navigator.share) {
      try {
        await navigator.share({ title, text: shareText, url: shareUrl });
        return;
      } catch (e) {
        if (e.name === "AbortError") return;
      }
    }
    // Desktop fallback
    setShowShare((p) => !p);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      showSuccess("Link copied!");
    } catch {
      showError("Could not copy link");
    }
    setShowShare(false);
  };

  const shareLinks = [
    {
      name: "WhatsApp",
      url: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
    },
    {
      name: "Facebook",
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    },
    {
      name: "X (Twitter)",
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    },
    {
      name: "Telegram",
      url: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
    },
  ];

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  /******************* THUMB RENDER *******************/
  const renderThumb = (item, i) => (
    <div
      key={i}
      className={`custom-thumb-box ${mainImage === item.src ? "active" : ""}`}
      onClick={() => setMainImage(item.src)}
    >
      {item.type === "video" ? (
        <div className="thumb-video-wrap">
          {isYouTube(item.src) ? (
            <img
              src={`https://img.youtube.com/vi/${getYouTubeID(item.src)}/hqdefault.jpg`}
              alt="video"
              className="img-fluid"
            />
          ) : (
            <video src={`${item.src}#t=0.5`} preload="metadata" muted />
          )}
          <span className="thumb-play-icon">
            <Play size={16} fill="#fff" />
          </span>
        </div>
      ) : (
        <img src={item.src} alt={`thumb-${i}`} className="img-fluid" />
      )}
    </div>
  );

  return (
    <>
      <Breadcrump title={product?.title} />
      <SEO meta={detail?.meta} />

      <section className="custom-detail-hero py-5">
        <div className="container">
          <div className="row g-3">
            {/* Left: Gallery */}
            <div className="col-lg-7">
              <div className="custom-gallery-sticky">
                <div className="row g-3">
                  {/* Desktop thumbnails (images + video) */}
                  <div className="col-2 d-none d-md-block">
                    <div className="d-flex flex-column gap-3 thumb-column">
                      {mediaItems.map(renderThumb)}
                    </div>
                  </div>

                  {/* Main viewer */}
                  <div className="col-md-10">
                    <div className="custom-main-img-wrapper shadow-sm">
                      {isMainVideo ? (
                        isYouTube(mainImage) ? (
                          <iframe
                            src={`https://www.youtube.com/embed/${getYouTubeID(mainImage)}`}
                            title="Product video"
                            allow="autoplay; encrypted-media"
                            allowFullScreen
                            className="main-video"
                          />
                        ) : (
                          <video
                            src={mainImage}
                            controls
                            autoPlay
                            muted
                            playsInline
                            className="main-video"
                          />
                        )
                      ) : (
                        mainImage && (
                          <img
                            src={mainImage}
                            alt="Main Product"
                            className="main-img"
                          />
                        )
                      )}

                      <div className="img-overlay-tag">Luxury Edition</div>
                    </div>
                  </div>

                  {/* Mobile thumbnails (images + video) */}
                  <div className="d-md-none d-block">
                    <div className="d-flex flex-row gap-3 thumb-row-mobile">
                      {mediaItems.map(renderThumb)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Sticky Info */}
            <div className="col-lg-5">
              <div className="custom-detail-sticky-info">
                <div className="d-flex align-items-center gap-2 mb-2">
                  {brand && (
                    <span>
                      <Link
                        to={`/brands/${brand?.slug}`}
                        className="rating-pill text-decoration-none"
                      >
                        {brand?.name}
                      </Link>
                    </span>
                  )}

                  <div className="product-stock">
                    {selectedVariant && selectedVariant.stock > 0 ? (
                      <span className="stock-in small fw-bold">In Stock</span>
                    ) : (
                      <span className="stock-out small fw-bold">
                        Out of Stock
                      </span>
                    )}
                  </div>
                </div>

                <h1 className="custom-detail-title mb-3">{title}</h1>
                <h2 className="custom-detail-price mb-4">
                  ₹{selectedVariant ? selectedVariant.price : price}
                  {selectedVariant?.mrp > selectedVariant?.price && (
                    <span className="old-price">₹{selectedVariant.mrp}</span>
                  )}
                </h2>

                {(product.skuCode || category) && (
                  <div className="item">
                    <ul className="p-0">
                      {product.skuCode && (
                        <li>
                          <b className="fw-bold detail-label-accent">SKU :</b>{" "}
                          {product.skuCode}
                        </li>
                      )}
                      {category && (
                        <li>
                          <b className="fw-bold detail-label-accent">
                            Category :
                          </b>{" "}
                          <Link
                            to={`/categories/${category?.slug}`}
                            className="text-decoration-none text-muted"
                          >
                            {category.name}
                          </Link>
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                {selectedVariant && selectedVariant.stock <= 100 && (
                  <div className="product-progress-sale">
                    <div className="title-hurry-up">
                      <span className="hurry-text fw-medium">HURRY UP!</span>{" "}
                      Only{" "}
                      <span className="count">{selectedVariant.stock}</span>{" "}
                      items left!
                    </div>
                  </div>
                )}

                <hr />
                {detail?.shortDescription && (
                  <p className="custom-detail-short-desc mb-4">
                    {detail?.shortDescription || description}
                  </p>
                )}

                <div className="tf-product-variant">
                  {Object.entries(attributeMap).map(([attrKey, values]) => {
                    const valueArray = [...values];

                    return (
                      <div
                        className="variant-size variant-picker-item"
                        key={attrKey}
                      >
                        <div className="variant-picker-label">
                          {attrKey.replace("variantAttribute", "Option")}:{" "}
                          <span className="variant-picker-label-value">
                            {selectedAttributes[attrKey] || "Select"}
                          </span>
                        </div>

                        <div className="variant-picker-values">
                          {valueArray.map((value, index) => {
                            const inputId = `${attrKey}-${value}-${index}`;
                            return (
                              <label
                                key={value}
                                htmlFor={inputId}
                                className={`variant-btn check-ship ${
                                  selectedAttributes[attrKey] === value
                                    ? "active"
                                    : ""
                                }`}
                              >
                                <input
                                  type="radio"
                                  id={inputId}
                                  name={attrKey}
                                  className="tf-check-rounded"
                                  value={value}
                                  checked={selectedAttributes[attrKey] === value}
                                  onChange={() =>
                                    handleVariantClick(attrKey, value)
                                  }
                                />
                                <span className="text text-sm">
                                  <span>{value}</span>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Add to Cart Section */}
                <div className="action-container mb-4">
                  <div className="custom-personalized-wrapper mb-4">
                    {product?.customFields?.map((custom) => (
                      <div key={custom.key} className="col-12 mb-2">
                        <div className="enquiry-form-group">
                          <label className="custom-personalized-title mb-0">
                            {custom.label}
                            {custom.required && " *"}
                          </label>
                          <p className="mb-2">{custom.sublabel}</p>
                          <DynamicField
                            field={custom}
                            value={formData[custom.key]}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <label className="small fw-bold mb-2 d-block text-muted">
                    Select Quantity{" "}
                    {product?.minimumOrderQuantity > 1 && (
                      <span className="text-secondary small mb-2">
                        (Minimum Order Quantity: {product.minimumOrderQuantity})
                      </span>
                    )}
                  </label>

                  <div className="d-flex gap-3 mb-3 detail-quantity flex-wrap">
                    <div className="qty-input-group border rounded-0 d-flex align-items-center">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          if (quantity > (product?.minimumOrderQuantity || 1)) {
                            setQuantity(quantity - 1);
                          }
                        }}
                      >
                        -
                      </button>
                      <span>{quantity}</span>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          setQuantity(quantity + 1);
                        }}
                      >
                        +
                      </button>
                    </div>

                    <button
                      type="button"
                      className={`detail-wishlist-btn flex-shrink-0 ${
                        isWishlisted ? "active" : ""
                      }`}
                      onClick={handleWishlistToggle}
                      aria-label="Add to wishlist"
                    >
                      <Heart size={24} className="custom-product-icon me-2" />
                      Wishlist
                    </button>

                    {/* SHARE */}
                    <div className="share-wrapper position-relative">
                      <button
                        type="button"
                        className="detail-wishlist-btn flex-shrink-0"
                        onClick={handleShare}
                        aria-label="Share product"
                      >
                        <Share2 size={22} className="me-2" />
                        Share
                      </button>

                      {showShare && (
                        <div className="share-dropdown">
                          {shareLinks.map((s) => (
                            <a
                              key={s.name}
                              href={s.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => setShowShare(false)}
                            >
                              {s.name}
                            </a>
                          ))}
                          <button type="button" onClick={copyLink}>
                            <Link2 size={14} className="me-1" />
                            Copy link (Instagram ke liye)
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn flex-grow-1 w-100 py-2 rounded-0 fw-bold btn-add-cart"
                    onClick={handleAddToCart}
                    data-action="cart"
                  >
                    <ShoppingBag size={18} className="me-2" />
                    Add to Basket
                  </button>
                  <a
                    href="#"
                    className="btn py-2 rounded-0 fw-bold btn-detail text-decoration-none btn-buy-now w-100 mt-2"
                    onClick={handleAddToCart}
                    data-action="buy-now"
                  >
                    Buy it now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT HIGHLIGHTS */}
      {detail?.keyFeatures?.length > 0 && (
        <section className="pf-wrap py-5">
          <div className="container">
            <div className="text-center mb-5">
              <span className="pf-subtitle">Specifications</span>
              <h2 className="custom-feature-heading">Product Highlights</h2>
            </div>

            <div className="spec-grid">
              {detail.keyFeatures.map((feature, index) => (
                <div className="spec-item" key={index}>
                  <span className="spec-label">{feature?.label}</span>
                  <span className="spec-value">{feature?.value}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* DESCRIPTION */}
      <section className="custom-desc-section pb-5">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2 className="custom-desc-heading mb-3 py-5">
                Product Description
              </h2>
              <div
                className="custom-desc-content"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* RELATED PRODUCTS */}
      {relatedProducts && relatedProducts.length > 0 && (
        <div className="related-products-section py-5">
          <div className="container product">
            <div className="flat-animate-tab flat-controltab-nav">
              <div className="text-start mb-4">
                <h4 className="section-title">Related Products</h4>
              </div>
              <div className="row">
                {relatedProducts.map((item, index) => (
                  <div
                    className="col-lg-3 col-md-6 col-6 mb-2"
                    key={item?._id || index}
                  >
                    <ProductCard item={item} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DetailPage;